/**
 * Admin triage list for coverage-misdirection reports (PROMPT 9 / §10).
 * Simple categorize UI: network / coverage / misunderstanding / dismissed.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { format } from "date-fns";
import { Controller, useForm } from "react-hook-form";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import "firebase/compat/functions";
import Loading from "components/Loading";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";

const TRIAGE_OPTIONS = [
  { value: "network_participation", label: "Network participation fault" },
  { value: "coverage_status", label: "Coverage status fault" },
  { value: "misunderstanding", label: "Misunderstanding" },
  { value: "dismissed", label: "Dismiss" },
];

const MisdirectionTriagePage = () => {
  const { isGlobalAdmin, userLoading } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busyId, setBusyId] = useState(null);

  const { control, getValues, reset } = useForm({
    defaultValues: {},
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const snap = await firebase
        .firestore()
        .collection("coverageMisdirectionReports")
        .orderBy("createdAt", "desc")
        .limit(100)
        .get();
      const rows = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setReports(rows);
      const defaults = {};
      rows.forEach((r) => {
        defaults[`cat_${r.id}`] = r.triageCategory || "";
      });
      reset(defaults);
    } catch (err) {
      setError(err?.message || "Failed to load reports.");
    } finally {
      setLoading(false);
    }
  }, [reset]);

  useEffect(() => {
    if (isGlobalAdmin) load();
    else setLoading(false);
  }, [isGlobalAdmin, load]);

  const onTriage = async (reportId) => {
    const triageCategory = getValues(`cat_${reportId}`);
    if (!triageCategory) {
      setError("Choose a triage category first.");
      return;
    }
    setBusyId(reportId);
    setError("");
    setSuccess("");
    try {
      const callable = firebase
        .functions()
        .httpsCallable("triageMisdirectionReport");
      await callable({ reportId, triageCategory });
      setSuccess(`Report ${reportId} categorized.`);
      await load();
    } catch (err) {
      setError(err?.message || "Triage failed.");
    } finally {
      setBusyId(null);
    }
  };

  if (userLoading || loading) return <Loading page />;
  if (!isGlobalAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Platform admin required.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }} data-testid="careverify-misdirection-triage">
      <DashboardPageHeader
        title="Coverage misdirection triage"
        subtitle="Categorize reports before they count against facility reputation"
      />
      {error ? (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" onClose={() => setSuccess("")} sx={{ mb: 2 }}>
          {success}
        </Alert>
      ) : null}
      <Paper sx={{ overflow: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Created</TableCell>
              <TableCell>Facility</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Narrative</TableCell>
              <TableCell>Categorize</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary">No reports.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              reports.map((r) => {
                const created =
                  r.createdAt?.toDate?.() ||
                  (r.createdAt ? new Date(r.createdAt) : null);
                return (
                  <TableRow key={r.id}>
                    <TableCell>
                      {created && !Number.isNaN(created.getTime())
                        ? format(created, "MMM d, yyyy HH:mm")
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.facilityId}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={r.status || "open"} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="body2" noWrap title={r.narrative}>
                        {r.narrative}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      <Controller
                        name={`cat_${r.id}`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            select
                            size="small"
                            fullWidth
                            label="Category"
                            {...field}
                            value={field.value || ""}
                          >
                            {TRIAGE_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <LoadingButton
                        size="small"
                        loading={busyId === r.id}
                        onClick={() => onTriage(r.id)}
                        disabled={r.status !== "open" && Boolean(r.triageCategory)}
                      >
                        Save
                      </LoadingButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Paper>
      <Stack direction="row" sx={{ mt: 2 }}>
        <LoadingButton onClick={load}>Refresh</LoadingButton>
      </Stack>
    </Box>
  );
};

export default MisdirectionTriagePage;
